/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

import ErrorName from '../error/error-name';

// TODO Rozšířit i o infomace přidané v Device#getLoggerInfo
// - Bude možné až po migraci všech platforem na multiplatformní Adapter.
// - Přidá položky: deviceId, online, version.
/**
 * Správce informací pro logování.
 */
class LoggerManager {
  /**
   * Vytvoří instanci.
   * @param {AppManager} appManager Správce aplikace.
   * @param {FirmwareManager} firmwareManager Správce firmware.
   * @param {Logger} logger
   * @returns {LoggerManager} Nová instance.
   */
  constructor(appManager, firmwareManager, logger) {
    this.appManager = appManager;
    this.firmwareManager = firmwareManager;
    this.logger = logger;
  }

  /**
   * Zjistí informace pro logování.
   * @returns {Promise.<LoggerInfo>} Informace.
   */
  getInfo() {
    return new Promise((resolve) =>
      resolve(
        [
          () => this.getManufacturer(),
          () => this.getModelName(),
          () => this.getRuntimePlatform(),
          () => this.getUpdatePlatform(),
          () => this.getUpdateUrl()
        ].reduce(
          (accPromise, getter) =>
            accPromise.then((accObject) =>
              getter()
                .then((result) => Object.assign(accObject, result))
                .catch((error) => {
                  // Chyby vycházející z nedostupnosti informací se ignorují.
                  if (error.name !== ErrorName.NotSupportedError) {
                    this.logger.error(error);
                  }
                  return accObject;
                })
            ),
          Promise.resolve({})
        )
      )
    );
  }

  /**
   * Zjistí část informací s výrobcem zařízení.
   * @private
   * @returns {Promise.<LoggerInfo>} Informace.
   */
  getManufacturer() {
    return new Promise((resolve) =>
      resolve(
        this.firmwareManager
          .getManufacturer()
          .then((manufacturer) => ({ manufacturer }))
      )
    );
  }

  /**
   * Zjistí část informací s modelem zařízení.
   * @private
   * @returns {Promise.<LoggerInfo>} Informace.
   */
  getModelName() {
    return new Promise((resolve) =>
      resolve(
        this.firmwareManager.getModelName().then((modelName) => ({ modelName }))
      )
    );
  }

  /**
   * Zjistí identifikátor platformy, na které ja aplikace momentálně spuštěná.
   * @private
   * @returns {Promise.<LoggerInfo>} Informace.
   */
  getRuntimePlatform() {
    return new Promise((resolve) =>
      resolve(
        this.appManager
          .getRuntimePlatform()
          .then((runtimePlatform) => ({ runtimePlatform }))
      )
    );
  }

  /**
   * Zjistí část informací s platformou pro aktualizace.
   * @private
   * @returns {Promise.<LoggerInfo>} Informace.
   */
  getUpdatePlatform() {
    return new Promise((resolve) =>
      resolve(
        this.appManager
          .getUpdatePlatform()
          .then((updatePlatform) => ({ updatePlatform }))
      )
    );
  }

  /**
   * Zjistí část informací s aktualizační URL.
   * @private
   * @returns {Promise.<LoggerInfo>} Informace.
   */
  getUpdateUrl() {
    return new Promise((resolve) =>
      resolve(
        this.appManager.getUpdateUrl().then((updateUrl) => ({ updateUrl }))
      )
    );
  }
}

export default LoggerManager;
